import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, LinearProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Stack, useTheme,
} from '@mui/material';
import {
  BarChart as StatsIcon,
  Business as TenantsIcon,
  People as PeopleIcon,
  TrendingUp as ActiveIcon,
} from '@mui/icons-material';
import { platformApi } from '../api/services';

interface PlatformStats {
  totalTenants: number;
  activeTenants: number;
  totalUsers: number;
  activeThisMonth: number;
  monthlyActiveUsers: number;
  grossRevenueRetention: number | null;
  netRevenueRetention: number | null;
  tenantsByPlan: { plan: string; count: number }[];
  recentTenants: { id: string; name: string; plan: string; status: string; createdAt: string }[];
  currentMonthUsage: {
    planGenerations: number;
    transcriptsProcessed: number;
    aiTokensUsed: number;
  };
}

function StatCard({ title, value, icon, color }: { title: string; value: number | string; icon: React.ReactNode; color: string }) {
  return (
    <Card sx={{ bgcolor: 'background.paper', height: '100%' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2.5 }}>
        <Box sx={{
          width: 48, height: 48, borderRadius: 2,
          bgcolor: `${color}15`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: color,
        }}>
          {icon}
        </Box>
        <Box>
          <Typography variant="h4" sx={{ color: 'text.primary', fontWeight: 700, lineHeight: 1.2 }}>
            {typeof value === 'number' ? value.toLocaleString() : value}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
            {title}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function PlatformOverviewPage() {
  const theme = useTheme();
  const PLAN_COLORS: Record<string, string> = {
    Starter: theme.palette.primary.main,
    Professional: theme.palette.success.main,
    Enterprise: theme.palette.warning.main,
    Custom: theme.palette.secondary.main,
  };

  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const raw = await platformApi.getStats();
      // Normalize the API response to match our interface
      const data: PlatformStats = {
        totalTenants: raw.totalTenants ?? 0,
        activeTenants: raw.activeTenants ?? 0,
        totalUsers: raw.totalUsers ?? 0,
        activeThisMonth: raw.currentMonthUsage?.activeExecutives ?? raw.activeUsers ?? 0,
        monthlyActiveUsers: raw.monthlyActiveUsers ?? 0,
        grossRevenueRetention: raw.grossRevenueRetention ?? null,
        netRevenueRetention: raw.netRevenueRetention ?? null,
        tenantsByPlan: raw.tenantsByPlan
          ? (Array.isArray(raw.tenantsByPlan)
              ? raw.tenantsByPlan
              : Object.entries(raw.tenantsByPlan).map(([plan, count]) => ({ plan, count: count as number })))
          : [],
        recentTenants: raw.recentTenants ?? [],
        currentMonthUsage: {
          planGenerations: raw.currentMonthUsage?.totalPlanGenerations ?? raw.currentMonthUsage?.planGenerations ?? 0,
          transcriptsProcessed: raw.currentMonthUsage?.totalTranscripts ?? raw.currentMonthUsage?.transcriptsProcessed ?? 0,
          aiTokensUsed: raw.currentMonthUsage?.totalAiTokens ?? raw.currentMonthUsage?.aiTokensUsed ?? 0,
        },
      };
      setStats(data);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load platform stats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <StatsIcon sx={{ color: 'error.main', fontSize: 28 }} />
        <Box>
          <Typography variant="h4">Platform Overview</Typography>
          <Typography variant="subtitle1" color="text.secondary">
            System-wide metrics and tenant activity
          </Typography>
        </Box>
      </Box>

      {loading && <LinearProgress color="error" sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {stats && (
        <Stack spacing={2.5}>
          {/* Stats Cards */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Total Tenants"
                value={stats.totalTenants}
                icon={<TenantsIcon />}
                color={theme.palette.primary.main}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Active Tenants"
                value={stats.activeTenants}
                icon={<ActiveIcon />}
                color={theme.palette.success.main}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Total Users"
                value={stats.totalUsers}
                icon={<PeopleIcon />}
                color={theme.palette.secondary.main}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Monthly Active Users"
                value={stats.monthlyActiveUsers}
                icon={<PeopleIcon />}
                color={theme.palette.warning.main}
              />
            </Grid>
          </Grid>

          {/* Revenue Retention */}
          <Card sx={{ bgcolor: 'background.paper' }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1, fontWeight: 600, fontSize: '0.75rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                Revenue Retention
              </Typography>
              <Stack direction="row" spacing={4} alignItems="center">
                <Box>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>GRR</Typography>
                  <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 700 }}>
                    {stats.grossRevenueRetention !== null ? `${stats.grossRevenueRetention}%` : 'Calculating...'}
                  </Typography>
                </Box>
                <Box sx={{ width: 1, height: 32, bgcolor: 'divider' }} />
                <Box>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>NRR</Typography>
                  <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 700 }}>
                    {stats.netRevenueRetention !== null ? `${stats.netRevenueRetention}%` : 'Calculating...'}
                  </Typography>
                </Box>
                {stats.grossRevenueRetention === null && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Insufficient data (need 3+ months of billing history)
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* Tenants by Plan + Current Month Usage */}
          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card sx={{ bgcolor: 'background.paper', height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: 'text.primary', fontWeight: 600 }}>
                    Tenants by Plan
                  </Typography>
                  {stats.tenantsByPlan.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Plan</TableCell>
                            <TableCell align="right" sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Tenants</TableCell>
                            <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600, width: '40%' }}>Distribution</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {stats.tenantsByPlan.map(row => {
                            const total = stats.tenantsByPlan.reduce((s, r) => s + r.count, 0);
                            const pct = total > 0 ? (row.count / total) * 100 : 0;
                            const color = PLAN_COLORS[row.plan] || theme.palette.primary.main;
                            return (
                              <TableRow key={row.plan}>
                                <TableCell>
                                  <Chip
                                    label={row.plan}
                                    size="small"
                                    sx={{ bgcolor: `${color}20`, color: color, fontWeight: 600 }}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                                    {row.count}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <LinearProgress
                                      variant="determinate"
                                      value={pct}
                                      sx={{
                                        flex: 1, height: 6, borderRadius: 3,
                                        bgcolor: (t) => t.palette.action.hover,
                                        '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 },
                                      }}
                                    />
                                    <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 32, textAlign: 'right' }}>
                                      {pct.toFixed(0)}%
                                    </Typography>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                      No tenant data available
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Card sx={{ bgcolor: 'background.paper', height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: 'text.primary', fontWeight: 600 }}>
                    Current Month Usage
                  </Typography>
                  <Stack spacing={2.5}>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>Plan Generations</Typography>
                        <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>
                          {stats.currentMonthUsage.planGenerations.toLocaleString()}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, stats.currentMonthUsage.planGenerations / 10)}
                        sx={{
                          height: 6, borderRadius: 3,
                          bgcolor: (t) => t.palette.action.hover,
                          '& .MuiLinearProgress-bar': { bgcolor: theme.palette.primary.main, borderRadius: 3 },
                        }}
                      />
                    </Box>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>Transcripts Processed</Typography>
                        <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600 }}>
                          {stats.currentMonthUsage.transcriptsProcessed.toLocaleString()}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, stats.currentMonthUsage.transcriptsProcessed / 5)}
                        sx={{
                          height: 6, borderRadius: 3,
                          bgcolor: (t) => t.palette.action.hover,
                          '& .MuiLinearProgress-bar': { bgcolor: theme.palette.success.main, borderRadius: 3 },
                        }}
                      />
                    </Box>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>AI Tokens Used</Typography>
                        <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 600 }}>
                          {stats.currentMonthUsage.aiTokensUsed.toLocaleString()}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, stats.currentMonthUsage.aiTokensUsed / 10000)}
                        sx={{
                          height: 6, borderRadius: 3,
                          bgcolor: (t) => t.palette.action.hover,
                          '& .MuiLinearProgress-bar': { bgcolor: theme.palette.warning.main, borderRadius: 3 },
                        }}
                      />
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Recent Tenants */}
          <Card sx={{ bgcolor: 'background.paper' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, color: 'text.primary', fontWeight: 600 }}>
                Recent Tenants
              </Typography>
              {stats.recentTenants.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Name</TableCell>
                        <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Plan</TableCell>
                        <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Created</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stats.recentTenants.map(tenant => (
                        <TableRow key={tenant.id} hover>
                          <TableCell sx={{ color: 'text.primary', fontWeight: 500 }}>{tenant.name}</TableCell>
                          <TableCell>
                            <Chip
                              label={tenant.plan}
                              size="small"
                              sx={{
                                bgcolor: `${PLAN_COLORS[tenant.plan] || theme.palette.primary.main}20`,
                                color: PLAN_COLORS[tenant.plan] || theme.palette.primary.main,
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={tenant.status}
                              size="small"
                              color={tenant.status === 'Active' ? 'success' : tenant.status === 'Suspended' ? 'error' : 'default'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {new Date(tenant.createdAt).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No tenants yet
                </Typography>
              )}
            </CardContent>
          </Card>
        </Stack>
      )}

      {!loading && !stats && !error && (
        <Card sx={{ bgcolor: 'background.paper' }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary">No platform data available</Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
